import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  Fragment,
} from 'react';
import { string, func } from 'prop-types';
import classNames from 'classnames/bind';
import { splitEvery, uniqWith, eqBy } from 'ramda';

import { getCarsByRef } from 'utils/getCarsByRef';
import { getDirectArticlesInfoOLD } from 'utils/getArtclesInfo';
import { gtmPushEvent } from 'utils/gtmPushEvent';
import { isGlobalSite } from 'utils/currentLanguage';
import { getBrowserHistory } from 'utils/historyPushSearch';
import { redirectToTechAssist } from 'utils/redirectToTechAssist';
import browserHistory, { isTechAssistPage, isBlock } from 'utils/browserHistory';
import { baiduTriggerEvent } from 'utils/baiduTriggerEvent';

import { StaticTextContext } from 'context/StaticTextContext';
import { SearchContext } from 'context/SearchContext';
import { CountryContext } from 'context/CountryContext';

import { withErrorBoundary } from 'containers/ErrorBoundary';

import Hint from 'components/Hint';
import CountrySelector from 'components/CountrySelector';

import SearchIcon from 'media/icons/search.icon.svg';
import styles from './SearchByRef.module.scss';
const cx = classNames.bind(styles);

const SearchByRef = ({ pageType, setRefData, setRefNoResults }) => {
  const [inputValue, setInputValue] = useState('');
  const { currentCountry } = useContext(CountryContext);
  const { labels } = useContext(StaticTextContext);
  const { setReference, reference } = useContext(SearchContext);
  const { search, hash } = browserHistory.location;
  let referenceRef = useRef();

  const updateInputValue = (event) => {
    setInputValue(event.target.value.trim());
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const searchObj = { reference: inputValue };

    // Send gtm search event
    const pushRef = {
      event: 'searchProductReference',
      reference: inputValue,
    };
    gtmPushEvent(pushRef);

    setReference(inputValue);
    setRefNoResults(false);

    if (pageType === 'home') {
      if (!isTechAssistPage) {
        redirectToTechAssist(getBrowserHistory(searchObj, search, hash));
        return null;
      }
      browserHistory.push({
        pathname: '/search',
        search: getBrowserHistory(searchObj, search, hash),
      });
    } else {
      requestData(inputValue);
    }
  };

  // Keep old logic for reference
  // eslint-disable-next-line no-unused-vars
  const getTypeNumber = (valueLength) => {
    if (valueLength === 6) {
      return 0;
    } else if (valueLength < 6) {
      return 2;
    } else {
      return 10;
    }
  };

  const requestData = (currentValue) => {
    if (!currentValue) {
      return;
    }
    const typeNumber = 10;

    getCarsByRef(currentValue, typeNumber, currentCountry)
      .then((response) => {
        const noResults = refSearchSuccess(response);

        // Trigger gtm no search result
        if (noResults)
          gtmPushEvent({
            event: 'techassistSearchNoResults',
            reference: currentValue,
          });

        // Trigger Baidu
        baiduTriggerEvent({
          event: 'searchTAByRef',
          payload: { reference: currentValue, noResults, currentCountry },
        });
      })
      .catch(() => {
        setRefData([]);
        setRefNoResults(true);

        // Trigger gtm no search result
        gtmPushEvent({
          event: 'techassistSearchNoResults',
          reference: currentValue,
        });

        // Trigger Baidu
        baiduTriggerEvent({
          event: 'searchTAByRef',
          payload: { reference: currentValue, noResults: true, currentCountry },
        });
      });
  };

  const refSearchSuccess = (response) => {
    let noResults = false;
    if (response.status === 200 && response.data.data) {
      const dataArray = response.data.data.array;
      const carsArray = dataArray.map((item) => item.articleId);

      // Because of API issues. We need to clean search ID's from duplicates.
      const carsArrayClean = uniqWith(eqBy(String))(carsArray);

      splitEvery(25, carsArrayClean).forEach((group) => {
        getDirectArticlesInfoOLD(
          {
            articleId: {
              array: group,
            },
            articleCountry: currentCountry,
          },
          carsDirectSearchSuccess
        );
      });
    } else {
      setRefData([]);
      setRefNoResults(true);
      noResults = true;
    }

    return noResults;
  };

  const carsDirectSearchSuccess = (response) => {
    response.data.array.forEach((element, index) => {
      response.data.array[index].assignedArticle = element.directArticle;
    });

    setRefData(response.data.array);
    setRefNoResults(false);
  };

  useEffect(() => {
    if (currentCountry !== '.com') {
      // @todo: check if we need commented lines.
      // setReference('');
      // setInputValue('');
      setRefData([]);
      setRefNoResults(false);
      !isBlock && referenceRef.current && referenceRef.current.focus();
    }
  }, [currentCountry]);

  useEffect(() => {
    setInputValue('');
    setRefData([]);
    setRefNoResults(false);

    if (reference) {
      setInputValue(reference);
      requestData(reference);
    }
  }, []);

  return (
    <form onSubmit={handleSearch} className={cx('search-form', pageType)}>
      {isGlobalSite && (
        <div className={cx('form-item')}>
          <CountrySelector />
        </div>
      )}
      <label htmlFor="search-ref" className={cx('label')}>
        {labels.searchReferenceLabel}
      </label>
      <div
        className={cx('form-wrapper', {
          'is-filled': inputValue.length > 0,
        })}
      >
        <div className={cx('form-item')}>
          <input
            className={cx('form-control')}
            type="text"
            id="search-ref"
            onChange={updateInputValue}
            value={inputValue}
            minLength={2}
            disabled={currentCountry === '.com'}
            placeholder={labels.searchReferencePlaceholder}
            autoFocus={!isBlock}
            ref={referenceRef}
            required
          />
          {pageType === 'home' && (
            <Hint className={cx('hint')}>
              <span className={cx('hint-text')}>
                {labels.tipReferenceInput}
              </span>
            </Hint>
          )}
        </div>
        <button
          className={cx('btn', 'ta-btn-primary')}
          disabled={inputValue.length < 2}
        >
          <span className={cx('btn-icon')}>
            <SearchIcon />
          </span>
          <span className={cx('btn-text')}>
            <Fragment>{labels.searchButtonText}</Fragment>
          </span>
        </button>
      </div>
    </form>
  );
};

SearchByRef.propTypes = {
  setRefData: func.isRequired,
  setRefNoResults: func.isRequired,
  pageType: string.isRequired,
};

export default withErrorBoundary(SearchByRef);



// WEBPACK FOOTER //
// ./src/components/SearchByRef/SearchByRef.js